import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Text,
  Flex,
  VStack,
  HStack,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Avatar,
  Progress,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Input,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Divider
} from '@chakra-ui/react';
import { ethers } from 'ethers';

interface WalletAsset {
  symbol: string;
  name: string;
  balance: number;
  value: number;
  price: number;
  change24h: number;
  logo?: string;
}

interface WalletData {
  address: string;
  totalValue: number;
  totalChange24h: number;
  assets: WalletAsset[];
}

interface PortfolioIntegrationProps {
  onWalletConnected?: (walletData: WalletData) => void;
}

const PortfolioIntegration: React.FC<PortfolioIntegrationProps> = ({
  onWalletConnected
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [walletData, setWalletData] = useState<WalletData | null>(null);
  const [loading, setLoading] = useState(false);
  const [customAddress, setCustomAddress] = useState('');
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // Check if MetaMask is already connected
  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    if (typeof window.ethereum !== 'undefined') {
      try {
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts.length > 0) {
          setIsConnected(true);
          setWalletAddress(accounts[0]);
          await fetchWalletData(accounts[0]);
        }
      } catch (error) {
        console.error('Error checking wallet connection:', error);
      }
    }
  };

  const connectWallet = async () => {
    // Check if MetaMask is installed
    if (!window.ethereum) {
      toast({
        title: 'MetaMask Not Found',
        description: 'Please install MetaMask browser extension to connect your wallet.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      // Open MetaMask download page
      window.open('https://metamask.io/download/', '_blank');
      return;
    }

    try {
      setLoading(true);
      
      // Request account access
      const accounts = await window.ethereum.request({ 
        method: 'eth_requestAccounts' 
      });
      
      if (accounts.length === 0) {
        throw new Error('No accounts found');
      }
      
      setIsConnected(true);
      setWalletAddress(accounts[0]);
      await fetchWalletData(accounts[0]);
      
      toast({
        title: 'Wallet Connected Successfully!',
        description: `Connected to ${accounts[0].slice(0, 6)}...${accounts[0].slice(-4)}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
    } catch (error: any) {
      console.error('Error connecting wallet:', error);
      
      let errorMessage = 'Failed to connect wallet. Please try again.';
      
      if (error.code === 4001) {
        errorMessage = 'Connection cancelled. Please approve the connection request.';
      } else if (error.code === -32002) {
        errorMessage = 'Connection request pending. Please check MetaMask.';
      }
      
      toast({
        title: 'Connection Failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const disconnectWallet = () => {
    setIsConnected(false);
    setWalletAddress('');
    setWalletData(null);
    
    toast({
      title: 'Wallet Disconnected',
      description: 'Your wallet has been disconnected.',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };

  const fetchWalletData = async (address: string) => {
    setLoading(true);
    try {
      // Mock wallet data - in production, this would call real APIs
      const mockData: WalletData = {
        address,
        totalValue: 125420.50,
        totalChange24h: 5.67,
        assets: [
          {
            symbol: 'ETH',
            name: 'Ethereum',
            balance: 25.5,
            value: 65280.75,
            price: 2560.50,
            change24h: 3.2,
            logo: '🔹'
          },
          {
            symbol: 'BTC',
            name: 'Bitcoin',
            balance: 1.2,
            value: 52800.00,
            price: 44000.00,
            change24h: 1.8,
            logo: '₿'
          },
          {
            symbol: 'USDC',
            name: 'USD Coin',
            balance: 5000.0,
            value: 5000.00,
            price: 1.00,
            change24h: 0.1,
            logo: '💵'
          },
          {
            symbol: 'MATIC',
            name: 'Polygon',
            balance: 3200.0,
            value: 2339.75,
            price: 0.73,
            change24h: 12.5,
            logo: '🟣'
          }
        ]
      };

      setWalletData(mockData);
      onWalletConnected?.(mockData);
    } catch (error) {
      console.error('Error fetching wallet data:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch wallet data.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const addCustomAddress = async () => {
    if (!customAddress) return;
    
    try {
      // Validate Ethereum address
      if (!ethers.isAddress(customAddress)) {
        toast({
          title: 'Invalid Address',
          description: 'Please enter a valid Ethereum address.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      await fetchWalletData(customAddress);
      setWalletAddress(customAddress);
      setIsConnected(true);
      onClose();
      setCustomAddress('');
      
      toast({
        title: 'Address Added',
        description: `Tracking wallet ${customAddress.slice(0, 6)}...${customAddress.slice(-4)}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error adding custom address:', error);
      toast({
        title: 'Error',
        description: 'Failed to add wallet address.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatNumber = (value: number, decimals = 4) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals
    }).format(value);
  };

  if (!isConnected) {
    return (
      <Box
        bg="white"
        p={8}
        borderRadius="lg"
        shadow="sm"
        border="1px solid"
        borderColor="gray.200"
        textAlign="center"
      >
        <Text fontSize="xl" fontWeight="bold" mb={4}>
          Portfolio Integration
        </Text>
        <Text color="gray.600" mb={6}>
          Connect your wallet to track your portfolio performance and get personalized insights.
        </Text>
        
        <VStack spacing={4}>
          <Button
            colorScheme="blue"
            size="lg"
            onClick={connectWallet}
            isLoading={loading}
            loadingText="Connecting..."
          >
            Connect MetaMask
          </Button>
          
          <Text fontSize="sm" color="gray.500">or</Text>
          
          <Button variant="outline" onClick={onOpen}>
            Add Wallet Address Manually
          </Button>
        </VStack>

        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Add Wallet Address</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text mb={4} color="gray.600">
                Enter an Ethereum wallet address to track:
              </Text>
              <Input
                placeholder="0x..."
                value={customAddress}
                onChange={(e) => setCustomAddress(e.target.value)}
              />
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={addCustomAddress}>
                Add Wallet
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Portfolio Overview */}
      <Box bg="white" p={6} borderRadius="lg" shadow="sm" border="1px solid" borderColor="gray.200">
        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          <Box>
            <Text fontSize="xl" fontWeight="bold">Portfolio Overview</Text>
            <Text fontSize="sm" color="gray.600">
              {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}
            </Text>
          </Box>
          <Button size="sm" variant="outline" onClick={disconnectWallet}>
            Disconnect
          </Button>
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          <Stat>
            <StatLabel>Total Portfolio Value</StatLabel>
            <StatNumber fontSize="2xl">
              {formatCurrency(walletData?.totalValue || 0)}
            </StatNumber>
            <StatHelpText>
              <StatArrow type={walletData && walletData.totalChange24h >= 0 ? 'increase' : 'decrease'} />
              {Math.abs(walletData?.totalChange24h || 0).toFixed(2)}% (24h)
            </StatHelpText>
          </Stat>

          <Stat>
            <StatLabel>Number of Assets</StatLabel>
            <StatNumber fontSize="2xl">{walletData?.assets.length || 0}</StatNumber>
            <StatHelpText>Across your portfolio</StatHelpText>
          </Stat>

          <Stat>
            <StatLabel>Best Performer</StatLabel>
            <StatNumber fontSize="lg">
              {walletData?.assets.reduce((best, asset) => 
                asset.change24h > best.change24h ? asset : best
              )?.symbol || 'N/A'}
            </StatNumber>
            <StatHelpText>
              <StatArrow type="increase" />
              {walletData?.assets.reduce((best, asset) => 
                asset.change24h > best.change24h ? asset : best
              )?.change24h.toFixed(2) || 0}%
            </StatHelpText>
          </Stat>
        </SimpleGrid>
      </Box>

      {/* Asset Breakdown */}
      <Box bg="white" p={6} borderRadius="lg" shadow="sm" border="1px solid" borderColor="gray.200">
        <Text fontSize="lg" fontWeight="bold" mb={4}>Asset Breakdown</Text>
        
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Asset</Th>
              <Th isNumeric>Balance</Th>
              <Th isNumeric>Price</Th>
              <Th isNumeric>Value</Th>
              <Th isNumeric>24h Change</Th>
              <Th isNumeric>Allocation</Th>
            </Tr>
          </Thead>
          <Tbody>
            {walletData?.assets.map((asset) => (
              <Tr key={asset.symbol}>
                <Td>
                  <HStack>
                    <Text fontSize="lg">{asset.logo}</Text>
                    <Box>
                      <Text fontWeight="medium">{asset.symbol}</Text>
                      <Text fontSize="sm" color="gray.600">{asset.name}</Text>
                    </Box>
                  </HStack>
                </Td>
                <Td isNumeric>{formatNumber(asset.balance)}</Td>
                <Td isNumeric>{formatCurrency(asset.price)}</Td>
                <Td isNumeric fontWeight="medium">{formatCurrency(asset.value)}</Td>
                <Td isNumeric>
                  <Badge
                    colorScheme={asset.change24h >= 0 ? 'green' : 'red'}
                    variant="subtle"
                  >
                    {asset.change24h >= 0 ? '+' : ''}{asset.change24h.toFixed(2)}%
                  </Badge>
                </Td>
                <Td isNumeric>
                  <VStack align="end" spacing={1}>
                    <Text fontSize="sm" fontWeight="medium">
                      {((asset.value / (walletData?.totalValue || 1)) * 100).toFixed(1)}%
                    </Text>
                    <Progress
                      value={(asset.value / (walletData?.totalValue || 1)) * 100}
                      size="sm"
                      w="60px"
                      colorScheme="blue"
                    />
                  </VStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Portfolio Insights */}
      <Box bg="white" p={6} borderRadius="lg" shadow="sm" border="1px solid" borderColor="gray.200">
        <Text fontSize="lg" fontWeight="bold" mb={4}>Portfolio Insights</Text>
        
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <Box p={4} bg="blue.50" borderRadius="md">
            <Text fontWeight="bold" color="blue.700" mb={2}>Diversification Score</Text>
            <Flex alignItems="center" gap={3}>
              <Progress value={75} size="lg" colorScheme="blue" flex={1} />
              <Text fontWeight="bold">75%</Text>
            </Flex>
            <Text fontSize="sm" color="blue.600" mt={2}>
              Well diversified across {walletData?.assets.length} assets
            </Text>
          </Box>

          <Box p={4} bg="green.50" borderRadius="md">
            <Text fontWeight="bold" color="green.700" mb={2}>Risk Assessment</Text>
            <Badge colorScheme="green" size="lg">MODERATE</Badge>
            <Text fontSize="sm" color="green.600" mt={2}>
              Balanced allocation between stable and growth assets
            </Text>
          </Box>
        </SimpleGrid>

        <Divider my={4} />

        <Text fontWeight="bold" mb={2}>Recommendations</Text>
        <VStack align="start" spacing={2}>
          <Text fontSize="sm" color="gray.600">
            • Consider rebalancing: ETH allocation is high at 52%
          </Text>
          <Text fontSize="sm" color="gray.600">
            • Strong performance in MATIC - consider taking partial profits
          </Text>
          <Text fontSize="sm" color="gray.600">
            • Stable allocation in USDC provides good downside protection
          </Text>
        </VStack>
      </Box>
    </VStack>
  );
};

export default PortfolioIntegration;